import React from "react";
import { Router, Switch, Route } from "react-router-dom";
import { Helmet } from "react-helmet";
import { createBrowserHistory } from "history";

// components:
import ErrorBoundary from "frontend/utils/ErrorBoundary";
import GoogleAnalaytics from "frontend/utils/GoogleAnalytics";
import SiteFooter from "core/SiteFooter";
import SiteHeaderParent from "core/SiteHeaderParent";
import NotFound from "core/NotFound";
import PhotosSearchPage from "photos/PhotoSearchPage";
import PhotoCheckoutPage from "photos/PhotoCheckoutPage";
import PhotoDetailPage from "photos/PhotoDetailPage";
import LoginRequired from "core/LoginRequired";

import { PhotosProvider } from "photos/context";
import Search from "icons/SearchPage";

// Photos related paths:
import {
    homePath,
    PRICING,
    SEARCH,
    PHOTO_CHECKOUT,
    PHOTO_DETAIL,
    BROWSE_BY_TAG,
    BROWSE_BY_SHOOT_ID,
    BROWSE_BY_CREATOR_USERNAME,
    BROWSE_SIMILAR_BY_PHOTO_ID,
    BROWSE_BY_CAMPAIGN,
    aboutPath,
    photosLandingPath,
    lingoMarketingPagePath,
    BECOME_PHOTOGRAPHER_LANDING_PAGE,
    CAREERS_PATH,
    NOUN_PRO_LANDING_PAGE,
    NOUN_PRO_CHECKOUT,
    NOUN_PRO_EDU_LANDING_PAGE,
    NOUN_PRO_TEAM_LANDING_PAGE,
    NOUN_PRO_CONFIRMATION_PAGE,
    NOUN_PRO_EDU_CHECKOUT,
    NOUN_PRO_EDU_CONFIRMATION_PAGE,
    NOUN_PRO_TEAM_CHECKOUT,
    NOUN_PRO_TEAM_CONFIRMATION_PAGE,
    NOUN_PRO_EDU_APPLICATION,
    EDU_APPLICATION_CONFIRMATION,
    NOUN_PRO_TEAM_INFO_PAGE,
    BROWSE_ICONS_SIMILAR,
    SEARCH_CONTENT
} from "data/links";

// Icons related paths:
import {
    ICON_DETAIL_PAGE,
    ICON_CHECKOUT,
    ICON_LANDING_PAGE,
    ICON_COLLECTION_SEARCH,
    CREATOR_COLLECTION_SEARCH
} from "data/links";

// new static/marketing pages:
import HomeNP from "marketing-pages/HomeNP";
import PricingMarketingPage from "marketing-pages/PricingMarketingPage";
import About from "marketing-pages/About";
import PhotosLanding from "marketing-pages/PhotosLanding";
import LingoMarketingPage from "marketing-pages/LingoMarketingPage";
import BecomeAPhotographer from "marketing-pages/BecomeAPhotographer";
import Careers from "marketing-pages/Careers";

// NounPro LP & Checkout Flows
import NounPro from "subscriptions/NounPro/LandingPage";
import NounProCheckout from "subscriptions/NounPro/Checkout";
import NounProConfirmation from "subscriptions/NounPro/Confirmation";

import NounProEdu from "subscriptions/NounProEdu/LandingPage";
import NounProEduCheckout from "subscriptions/NounProEdu/Checkout";
import NounProEduConfirmation from "subscriptions/NounProEdu/Confirmation";
import EduApplication from "subscriptions/NounProEdu/EduApplication";
import EduConfirmation from "subscriptions/NounProEdu/EduApplication/EduConfirmation";

import NounProTeam from "subscriptions/NounProTeam/LandingPage";
import NounProTeamCheckout from "subscriptions/NounProTeam/Checkout";
import NounProTeamConfirmation from "subscriptions/NounProTeam/Confirmation";
import TeamInfo from "subscriptions/NounProTeam/TeamInfo";

// icons related pages:
import IconLandingPage from "marketing-pages/Icons";
import IconDetailPage from "icons/IconDetailPage";
import IconCheckoutPage from "icons/IconCheckoutPage";
import CollectionSearch from "icons/CollectionSearch";
import CreatorCollectionSearch from "icons/CreatorCollectionSearch";

// temp
import BrowseIconsSimilar from "icons/BrowseIconsSimilar";

// styles:
import { SiteContentWrapper, ContentShifterForStickyHeader } from "core/Layout";
import colors from "core/colors";

const LoginRequiredPhotoCheckoutPage = props => (
    <LoginRequired component={PhotoCheckoutPage} {...props} />
);

const LoginRequiredIconCheckoutPage = props => (
    <LoginRequired component={IconCheckoutPage} {...props} />
);

const LoginRequiredNounProCheckoutPage = props => (
    <LoginRequired component={NounProCheckout} {...props} />
);

const LoginRequiredNounProEduCheckoutPage = props => (
    <LoginRequired component={NounProEduCheckout} {...props} />
);

const LoginRequiredNounProEduApplication = props => (
    <LoginRequired component={EduApplication} {...props} />
);

const LoginRequiredNounProTeamCheckoutPage = props => (
    <LoginRequired component={NounProTeamCheckout} {...props} />
);

const LoginRequiredNounProTeamInfoPage = props => (
    <LoginRequired component={TeamInfo} {...props} />
);

export const photoRoutes = [
    {
        path: SEARCH,
        component: PhotosSearchPage
    },
    {
        /* SiteHeaderParent and ContentShifterForStickyHeader
        are hidden on checkout page */
        path: PHOTO_CHECKOUT,
        component: LoginRequiredPhotoCheckoutPage
    },
    {
        path: PHOTO_DETAIL,
        component: PhotoDetailPage
    },
    {
        path: BROWSE_BY_TAG,
        component: PhotosSearchPage
    },
    {
        path: BROWSE_BY_SHOOT_ID,
        component: PhotosSearchPage
    },
    {
        path: BROWSE_BY_CREATOR_USERNAME,
        component: PhotosSearchPage
    },
    {
        path: BROWSE_SIMILAR_BY_PHOTO_ID,
        component: PhotosSearchPage
    },
    {
        path: BROWSE_BY_CAMPAIGN,
        component: PhotosSearchPage
    }
];

export const iconRoutes = [
    {
        path: SEARCH_CONTENT,
        component: Search
    },
    {
        path: ICON_LANDING_PAGE,
        component: IconLandingPage
    },
    {
        path: ICON_DETAIL_PAGE,
        component: IconDetailPage
    },
    {
        path: ICON_CHECKOUT,
        component: LoginRequiredIconCheckoutPage
    },
    {
        path: BROWSE_ICONS_SIMILAR,
        component: BrowseIconsSimilar
    },
    {
        path: ICON_COLLECTION_SEARCH,
        component: CollectionSearch
    },
    {
        path: CREATOR_COLLECTION_SEARCH,
        component: CreatorCollectionSearch
    }
];

export const subscriptionRoutes = [
    {
        path: NOUN_PRO_LANDING_PAGE,
        component: NounPro
    },
    {
        path: NOUN_PRO_CHECKOUT,
        component: LoginRequiredNounProCheckoutPage
    },
    {
        path: NOUN_PRO_CONFIRMATION_PAGE,
        component: NounProConfirmation
    },
    {
        path: NOUN_PRO_EDU_LANDING_PAGE,
        component: NounProEdu
    },
    {
        path: NOUN_PRO_EDU_CHECKOUT,
        component: LoginRequiredNounProEduCheckoutPage
    },
    {
        path: NOUN_PRO_EDU_CONFIRMATION_PAGE,
        component: NounProEduConfirmation
    },
    {
        path: NOUN_PRO_EDU_APPLICATION,
        component: LoginRequiredNounProEduApplication
    },
    {
        path: EDU_APPLICATION_CONFIRMATION,
        component: EduConfirmation
    },
    {
        path: NOUN_PRO_TEAM_LANDING_PAGE,
        component: NounProTeam
    },
    {
        path: NOUN_PRO_TEAM_CHECKOUT,
        component: LoginRequiredNounProTeamCheckoutPage
    },
    {
        path: NOUN_PRO_TEAM_CONFIRMATION_PAGE,
        component: NounProTeamConfirmation
    },
    {
        path: NOUN_PRO_TEAM_INFO_PAGE,
        component: LoginRequiredNounProTeamInfoPage
    }
];

export const otherRoutes = [
    {
        path: homePath.url,
        component: HomeNP
    },
    {
        path: PRICING,
        component: PricingMarketingPage
    },
    {
        path: aboutPath,
        component: About
    },
    {
        path: photosLandingPath,
        component: PhotosLanding
    },
    {
        path: lingoMarketingPagePath,
        component: LingoMarketingPage
    },
    {
        path: BECOME_PHOTOGRAPHER_LANDING_PAGE,
        component: BecomeAPhotographer
    },
    {
        path: CAREERS_PATH,
        component: Careers
    },
    {
        path: "*",
        component: NotFound
    }
];

export const allRoutes = [
    ...photoRoutes,
    ...iconRoutes,
    ...subscriptionRoutes,
    ...otherRoutes
];

export const Routes = ({ routeData }) => {
    return (
        <Switch>
            {routeData &&
                routeData.map(({ path, component: Component }, idx) => {
                    // add paths here that should return true for isHeroSearchV2:
                    const isDarkHeroAndHeader =
                        path === ICON_LANDING_PAGE ||
                        path === homePath.url ||
                        path === photosLandingPath;

                    // determines if route is a checkout page & passes bool to styled component
                    const isCheckoutPage =
                        path === PHOTO_CHECKOUT ||
                        path === ICON_CHECKOUT ||
                        path === NOUN_PRO_CHECKOUT ||
                        path === NOUN_PRO_EDU_CHECKOUT ||
                        path === NOUN_PRO_TEAM_CHECKOUT;

                    return (
                        <Route
                            key={idx}
                            path={path}
                            render={props => (
                                <PhotosProvider {...props}>
                                    <SiteContentWrapper
                                        isCheckoutPage={isCheckoutPage}
                                    >
                                        <SiteHeaderParent
                                            isHeroSearchV2={isDarkHeroAndHeader}
                                            heroSearchBgColor={
                                                isDarkHeroAndHeader
                                                    ? colors.midnight
                                                    : ""
                                            }
                                        />
                                        <ContentShifterForStickyHeader
                                            location={location}
                                            heroSearchBgColor={
                                                isDarkHeroAndHeader &&
                                                colors.midnight
                                            }
                                        />
                                        <Component {...props} />
                                    </SiteContentWrapper>

                                    <SiteFooter />
                                </PhotosProvider>
                            )}
                        />
                    );
                })}
        </Switch>
    );
};

export const AppRouter = () => {
    const history = createBrowserHistory();

    return (
        <>
            <Helmet>
                <title>Noun Project</title>
            </Helmet>
            <ErrorBoundary>
                <Router history={history}>
                    <GoogleAnalaytics debug={false} />
                    <Routes routeData={allRoutes} />
                </Router>
            </ErrorBoundary>
        </>
    );
};
